import { AppId, Language } from '@shared/types'
import { useReportLandingUrl } from '@ui-common/hooks/useReportLandingUrl'
import { GlobalServiceProvider } from '@ui-common/state/GlobalServiceProvider'
import { LanguageCtx, resolveAndStoreLanguage, richTextConfig } from '@ui-common/utils'
import { BankBrandWrapper } from '@ui-components/brand/BankBrandWrapper'
import { GlobalStyle } from '@ui-components/global/CommonStyling'
import { JSX, PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { IntlProvider } from 'react-intl'

import messagesCommonFi from '../l10n/fi.json'
import messagesCommonSv from '../l10n/sv.json'
import { PageTitleProvider } from '../state/PageTitleProvider'
import { Translations } from '../types'
import { AppErrorBoundary } from './AppErrorBoundary'

export type CommonBankAppProps = PropsWithChildren<{
  appId: AppId
  translations: Translations
}>

export const CommonBankApp = (props: CommonBankAppProps): JSX.Element => {
  const { appId, translations, children } = props
  const [currentLang, setCurrentLang] = useState<Language>(resolveAndStoreLanguage)

  useEffect(() => {
    document.documentElement.lang = currentLang
  }, [currentLang])

  useReportLandingUrl()

  const languageProviderValue = useMemo(() => ({ currentLang, setCurrentLang }), [currentLang])

  return (
    <BankBrandWrapper>
      <AppErrorBoundary>
        <LanguageCtx value={languageProviderValue}>
          <IntlProvider
            locale={currentLang}
            messages={{
              ...translations[currentLang],
              ...(currentLang === 'fi' ? messagesCommonFi : messagesCommonSv),
            }}
            defaultRichTextElements={richTextConfig}
          >
            <PageTitleProvider appId={appId}>
              <GlobalServiceProvider>
                <GlobalStyle />
                {children}
              </GlobalServiceProvider>
            </PageTitleProvider>
          </IntlProvider>
        </LanguageCtx>
      </AppErrorBoundary>
    </BankBrandWrapper>
  )
}
